import { useCallback, useEffect, useState } from 'react'

export interface AppUpdateInfo {
  version: string
  title: string
  description: string
}

const CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

/**
 * Detects a new production deployment by comparing the build version baked into
 * this bundle (__APP_VERSION__) against /meta.json served by the current deploy.
 *
 * - First-time visitors always download the latest build, so versions match and
 *   no banner is shown.
 * - After a deploy, versions diverge for users on the old bundle and the banner
 *   appears (within 5 minutes, or immediately when the tab regains focus).
 * - A refresh loads the new bundle, versions match again and the banner is gone.
 * - Dismissal is per-version: dismissing the banner for one deploy still lets
 *   the banner reappear for the next deploy in a long-lived tab.
 */
export function useAppUpdate() {
  const [update, setUpdate] = useState<AppUpdateInfo | null>(null)
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const response = await fetch(`/meta.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!response.ok) return
        const meta = await response.json()
        if (cancelled || !meta?.version || meta.version === __APP_VERSION__) return

        // Keep the previous object when the version is unchanged to avoid re-renders
        setUpdate((prev) =>
          prev?.version === meta.version
            ? prev
            : {
                version: meta.version,
                title: meta.title || 'New update available',
                description: meta.description || 'Refresh the page to get the latest version.',
              }
        )
      } catch {
        // Offline or transient network error - skip this check
      }
    }

    // Initial check catches the case where the service worker served a stale shell
    void check()
    const interval = setInterval(() => void check(), CHECK_INTERVAL)
    const handleFocus = () => void check()
    window.addEventListener('focus', handleFocus)

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const refresh = useCallback(async () => {
    setRefreshing(true)

    // The app shell (index.html + hashed assets) is served from the Workbox
    // *precache* of the currently active service worker. A plain reload therefore
    // re-serves the old bundle even after a new deploy - which is why users had to
    // hard-refresh (Ctrl+Shift+R) to bypass the SW cache. Reproduce that bypass
    // deterministically: drop the precache and the app worker, then reload so the
    // browser fetches the fresh index.html (and, via it, the new hashed assets).
    try {
      // Clear all Cache Storage entries (Workbox precache + font runtime caches).
      // These hold only static assets, so wiping them is safe.
      if ('caches' in window) {
        const keys = await caches.keys()
        await Promise.all(keys.map((key) => caches.delete(key)))
      }

      // Unregister the app service worker so it stops serving the stale shell.
      // Leave the Firebase messaging worker in place so push notifications survive.
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(
          registrations.filter((r) => !(r.active?.scriptURL || '').includes('firebase')).map((r) => r.unregister())
        )
      }
    } catch {
      // Ignore - reload regardless so the button never hangs on "Updating...".
    }

    window.location.reload()
  }, [])

  const visibleUpdate = update && update.version !== dismissedVersion ? update : null

  return {
    update: visibleUpdate,
    refreshing,
    dismiss: () => setDismissedVersion(update?.version ?? null),
    refresh,
  }
}
